import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import {
  ErrorBoundary,
  RPCs,
  RPCDocs,
  useRPC,
  ActivityTypeTagsHorizontalBarChart,
  EventTypesPieChart,
  EventTypesPieChartProps,
  DayOfWeekBarChart,
  HourOfDayBarChart,
  EventTypesStackedBarChart,
  EventTypesStackedBarChartProps,
} from '@therms/react-modules'
import { Icon, Loading } from '@therms/atalaya'
import { MdAreaChart } from 'react-icons/md'

interface EventsHistogramsProps {
  endDate?: string
  locationAndGroupIds?: string[]
  startDate: string
}

function EventsHistograms({
  endDate = new Date().toISOString(),
  locationAndGroupIds,
  startDate,
}: EventsHistogramsProps) {
  const calendarInterval: 'day' | 'month' = 'day'

  const { data } = useRPC<
    RPCDocs.Stats.GetEventsHistogram.v1.Args,
    RPCDocs.Stats.GetEventsHistogram.v1.Data
  >(
    RPCs.Stats.GetEventsHistogram.v1,
    {
      calendarInterval: calendarInterval as any,
      fromDate: startDate,
      locationAndGroupIds:
        locationAndGroupIds && locationAndGroupIds?.length > 0
          ? locationAndGroupIds
          : undefined,
      toDate: endDate,
    },
    {
      cacheStrategy: 'cache-only',
      timeout: 60_000,
      transport: 'http',
    },
  )

  const chartData = useMemo(() => {
    if (!data) return undefined

    const eventTypesAggMap = new Map<
      string,
      { color?: string; count: number; name: string }
    >()

    const eventTypesHistogram: EventTypesStackedBarChartProps['eventsHistogram'] =
      Object.entries(data.histogram).map(([isoDate, { count, eventTypes }]) => {
        eventTypes.forEach((eventType) => {
          const eventTypeData = eventTypesAggMap.get(eventType.name) || {
            color: eventType.color,
            count: 0,
            name: eventType.name,
          }

          eventTypeData.count += eventType.count

          eventTypesAggMap.set(eventType.name, eventTypeData)
        })

        return {
          count,
          eventTypes,
          formattedDate:
            calendarInterval === 'day'
              ? dayjs(isoDate).format('M/D')
              : dayjs(isoDate).format('MMM'),
          isoDateString: isoDate,
        }
      })

    const eventTypeCount: EventTypesPieChartProps['eventTypeCount'] =
      Array.from(eventTypesAggMap.values()).map(({ color, count, name }) => ({
        color,
        count,
        eventType: name,
      }))

    return {
      eventTypeCount,
      eventTypesHistogram,
    }
  }, [data])

  if (data && !data.totalCount) {
    return (
      <div className="mt-xxl flex flex-col items-center text-lg text-neutral">
        <Icon Icon={MdAreaChart} size="xxl" />
        <div>No Event stats to show</div>
      </div>
    )
  }

  return (
    <div className="relative">
      {!chartData || !data ? (
        <div className="flex justify-center">
          <Loading size="lg" />
        </div>
      ) : (
        <div className="space-y-base">
          <ErrorBoundary>
            <EventTypesStackedBarChart
              eventsHistogram={chartData.eventTypesHistogram}
            />
          </ErrorBoundary>

          <ErrorBoundary>
            <EventTypesPieChart eventTypeCount={chartData.eventTypeCount} />
          </ErrorBoundary>

          <ErrorBoundary>
            <DayOfWeekBarChart dowCount={data.dow as any} />
          </ErrorBoundary>

          <ErrorBoundary>
            <HourOfDayBarChart hourCount={data.hour as any} />
          </ErrorBoundary>

          <ErrorBoundary>
            <ActivityTypeTagsHorizontalBarChart
              activityTypeTags={data.activityTypeTagCount}
            />
          </ErrorBoundary>
        </div>
      )}
    </div>
  )
}

export { EventsHistograms }
